// iOS Safe Area utilities for notched devices
export const IOSSafeArea = {
  // Check if device has safe area support
  hasSafeAreaSupport(): boolean {
    return CSS.supports('padding-top: env(safe-area-inset-top)');
  },

  // Check if running as iOS PWA
  isIOSStandalone(): boolean {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = (window.navigator as any).standalone === true ||
                        window.matchMedia('(display-mode: standalone)').matches;
    return isIOS && isStandalone;
  },

  // Get safe area values (returns 0 if not supported)
  getSafeAreaInsets(): {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } {
    if (!this.hasSafeAreaSupport()) {
      return { top: 0, bottom: 0, left: 0, right: 0 };
    }

    // Create temporary element to measure safe area
    const testElement = document.createElement('div');
    testElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);
      padding-left: env(safe-area-inset-left);
      padding-right: env(safe-area-inset-right);
      visibility: hidden;
      pointer-events: none;
    `;
    
    document.body.appendChild(testElement);
    
    const computedStyle = getComputedStyle(testElement);
    const insets = {
      top: parseInt(computedStyle.paddingTop) || 0,
      bottom: parseInt(computedStyle.paddingBottom) || 0,
      left: parseInt(computedStyle.paddingLeft) || 0,
      right: parseInt(computedStyle.paddingRight) || 0,
    };
    
    document.body.removeChild(testElement);
    return insets;
  },

  // Apply safe area CSS custom properties
  applySafeAreaCSS(): void {
    if (!this.hasSafeAreaSupport()) return;

    const style = document.createElement('style');
    style.id = 'ios-safe-area-css';
    style.textContent = `
      :root {
        --safe-area-inset-top: env(safe-area-inset-top, 0px);
        --safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
        --safe-area-inset-left: env(safe-area-inset-left, 0px);
        --safe-area-inset-right: env(safe-area-inset-right, 0px);
      }
      
      /* Utility classes for safe area */
      .safe-area-top { padding-top: var(--safe-area-inset-top); }
      .safe-area-bottom { padding-bottom: var(--safe-area-inset-bottom); }
      .safe-area-left { padding-left: var(--safe-area-inset-left); }
      .safe-area-right { padding-right: var(--safe-area-inset-right); }
      .safe-area-all { 
        padding-top: var(--safe-area-inset-top);
        padding-bottom: var(--safe-area-inset-bottom);
        padding-left: var(--safe-area-inset-left);
        padding-right: var(--safe-area-inset-right);
      }
      
      /* Fixed positioning adjustments for iOS PWA */
      .ios-fixed-top {
        top: var(--safe-area-inset-top);
      }
      
      .ios-fixed-bottom {
        bottom: var(--safe-area-inset-bottom);
      }
      
      /* Update banner safe area adjustments */
      #pwa-update-banner {
        padding-top: calc(12px + var(--safe-area-inset-top));
      }
      
      /* Install prompt safe area adjustments */
      .pwa-install-prompt {
        bottom: calc(16px + var(--safe-area-inset-bottom));
        left: calc(16px + var(--safe-area-inset-left));
        right: calc(16px + var(--safe-area-inset-right));
      }
      
      /* Dashboard navigation safe area */
      .dashboard-nav {
        padding-top: var(--safe-area-inset-top);
      }
      
      /* Prevent content from going behind notch */
      .ios-content-safe {
        margin-top: var(--safe-area-inset-top);
        margin-bottom: var(--safe-area-inset-bottom);
      }
      
      /* iOS specific overscroll behavior */
      body.ios-standalone {
        overscroll-behavior: none;
        -webkit-overflow-scrolling: touch;
      }
      
      /* Prevent zoom on inputs in iOS PWA */
      @media screen and (max-width: 768px) {
        body.ios-standalone input,
        body.ios-standalone textarea,
        body.ios-standalone select {
          font-size: 16px !important;
        }
      }
    `;
    
    // Remove existing safe area CSS if present
    const existing = document.getElementById('ios-safe-area-css');
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(style);
  },

  // Initialize iOS PWA enhancements
  initialize(): void {
    if (!this.isIOSStandalone()) return;

    // Apply safe area CSS
    this.applySafeAreaCSS();
    
    // Add iOS standalone class to body
    document.body.classList.add('ios-standalone');
    
    // Fix viewport for iOS PWA
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
      );
    }
    
    // Prevent bounce scrolling
    document.body.style.overscrollBehavior = 'none';
    
    // Log safe area values for debugging
    const insets = this.getSafeAreaInsets();
    console.log('[iOS PWA] Safe area insets:', insets);
    
    // Report to analytics
    if (window.gtag) {
      window.gtag('event', 'pwa_ios_safe_area_detected', {
        event_category: 'PWA',
        event_label: 'iOS Safe Area',
        custom_parameters: {
          top: insets.top,
          bottom: insets.bottom,
          left: insets.left,
          right: insets.right
        }
      });
    }
  },

  // Test safe area rendering (for visual regression testing)
  createTestOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.id = 'safe-area-test-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      z-index: 9999;
      border-top: 2px solid red;
      border-bottom: 2px solid red;
      border-left: 2px solid red;
      border-right: 2px solid red;
      margin-top: var(--safe-area-inset-top, 0px);
      margin-bottom: var(--safe-area-inset-bottom, 0px);
      margin-left: var(--safe-area-inset-left, 0px);
      margin-right: var(--safe-area-inset-right, 0px);
    `;
    
    // Add text overlay showing safe area values
    const insets = this.getSafeAreaInsets();
    const textOverlay = document.createElement('div');
    textOverlay.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 10px;
      border-radius: 5px;
      font-family: monospace;
      font-size: 12px;
      text-align: center;
    `;
    textOverlay.innerHTML = `
      Safe Area Insets:<br>
      Top: ${insets.top}px<br>
      Bottom: ${insets.bottom}px<br>
      Left: ${insets.left}px<br>
      Right: ${insets.right}px
    `;
    
    overlay.appendChild(textOverlay);
    return overlay;
  },

  // Remove test overlay
  removeTestOverlay(): void {
    const overlay = document.getElementById('safe-area-test-overlay');
    if (overlay) {
      overlay.remove();
    }
  }
};

// Auto-initialize on page load
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => IOSSafeArea.initialize());
  } else {
    IOSSafeArea.initialize();
  }
}
